import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

export function Container({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'light' | 'outline-light';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-lg shadow-brand-primary/20 hover:brightness-110 active:brightness-95',
  secondary: 'bg-white text-text-primary border border-glass-border-bright hover:border-brand-primary/40 hover:text-brand-primary',
  ghost: 'text-brand-primary hover:bg-brand-primary/8',
  light: 'bg-white text-navy-900 hover:bg-white/90',
  'outline-light': 'border border-white/30 text-white hover:bg-white/10',
};

interface ButtonProps {
  to?: string;
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  arrow?: boolean;
  className?: string;
  children: ReactNode;
  download?: boolean;
  /** External links open in a new tab unless this is set (e.g. a purchase flow on a sister site). */
  sameTab?: boolean;
}

export function Button({
  to,
  href,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled,
  arrow,
  className = '',
  children,
  download,
  sameTab,
}: ButtonProps) {
  const sizes = { sm: 'px-3.5 py-2 text-xs', md: 'px-5 py-2.5 text-sm', lg: 'px-7 py-3.5 text-sm' };
  const cls = `inline-flex items-center justify-center gap-2 rounded-[var(--radius-glass-sm)] font-semibold transition-all disabled:opacity-50 disabled:pointer-events-none ${sizes[size]} ${VARIANTS[variant]} ${className}`;
  const content = (
    <>
      {children}
      {arrow && <ArrowRight size={16} className="shrink-0" />}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls}>
        {content}
      </Link>
    );
  }
  if (href) {
    const external = /^https?:/.test(href) && !sameTab;
    return (
      <a
        href={href}
        className={cls}
        download={download}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {content}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={cls}>
      {content}
    </button>
  );
}

export function TextLink({ to, children, className = '' }: { to: string; children: ReactNode; className?: string }) {
  const cls = `inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:gap-2.5 transition-all ${className}`;
  if (/^https?:/.test(to)) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>
        {children}
        <ArrowRight size={14} />
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {children}
      <ArrowRight size={14} />
    </Link>
  );
}

export function Eyebrow({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] ${
        dark ? 'text-brand-accent' : 'text-brand-primary'
      }`}
    >
      <span className={`h-px w-6 ${dark ? 'bg-brand-accent' : 'bg-brand-primary'}`} />
      {children}
    </div>
  );
}

export function GradientText({ children }: { children: ReactNode }) {
  return (
    <span className="bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent">{children}</span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  intro,
  align = 'left',
  dark = false,
  action,
}: {
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
  align?: 'left' | 'center';
  dark?: boolean;
  action?: ReactNode;
}) {
  const centered = align === 'center';
  return (
    <div
      className={`mb-12 flex flex-col gap-6 ${centered ? 'items-center text-center' : 'lg:flex-row lg:items-end lg:justify-between'}`}
    >
      <div className={centered ? 'max-w-3xl' : 'max-w-3xl'}>
        {eyebrow && (
          <div className="mb-4">
            <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
          </div>
        )}
        <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight leading-tight ${dark ? 'text-white' : 'text-text-primary'}`}>
          {title}
        </h2>
        {intro && <p className={`mt-4 text-lg leading-relaxed ${dark ? 'text-slate-300' : 'text-text-secondary'}`}>{intro}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/** Fades its children in once they scroll into view. */
export function Reveal({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  const { ref, isVisible } = useInView(0.1);
  return (
    <div ref={ref} className={`reveal ${isVisible ? 'visible' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export function IconTile({ icon: Icon, color, size = 'md' }: { icon: React.ComponentType<{ size?: number }>; color: string; size?: 'sm' | 'md' | 'lg' }) {
  const box = { sm: 'w-9 h-9', md: 'w-11 h-11', lg: 'w-14 h-14' }[size];
  const px = { sm: 18, md: 22, lg: 28 }[size];
  return (
    <div
      className={`${box} rounded-[var(--radius-glass-sm)] flex items-center justify-center shrink-0`}
      style={{ backgroundColor: `${color}14`, color }}
    >
      <Icon size={px} />
    </div>
  );
}

export function CheckList({ items, color = '#3b82f6', dark = false }: { items: string[]; color?: string; dark?: boolean }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className={`flex items-start gap-2.5 text-sm leading-relaxed ${dark ? 'text-slate-300' : 'text-text-secondary'}`}>
          <svg viewBox="0 0 16 16" className="w-4 h-4 mt-0.5 shrink-0" aria-hidden>
            <circle cx="8" cy="8" r="8" fill={color} fillOpacity="0.14" />
            <path d="M4.5 8.2l2.2 2.2 4.8-4.8" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
